import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, Pencil } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PageFrame } from '../components/work/page-chrome';
import { ErrorAlert, ListEmpty, LoadingRows } from '../components/work/page-states';
import { ReadinessBar, StageChip } from '../components/work/ServiceRequestBadges';
import ServiceRequestNav from '../components/work/ServiceRequestNav';
import { apiErrorMessage } from '../lib/api';
import { isOverdue, startOfDay } from '../lib/assigned-queue';
import { formatDateOnly } from '../lib/dates';
import {
  fetchImplementation,
  implementationPath,
  formatPercent,
  ragDotClass,
  SERVICE_REQUEST_STAGES,
  SR_PANEL,
  updateImplementation,
  type ImplementationDetail,
  type ImplementationRequest,
  type ServiceRequestBackTarget,
} from '../lib/service-request-views';
import { serviceRequestPath } from '../lib/service-requests';
import { cn } from '../lib/utils';

function DetailsForm({
  detail,
  onSaved,
  onCancel,
}: {
  detail: ImplementationDetail;
  onSaved: (next: ImplementationDetail) => void;
  onCancel: () => void;
}) {
  const { implementation } = detail;
  const [name, setName] = useState(implementation.name);
  const [customer, setCustomer] = useState(implementation.customer ?? '');
  const [targetDate, setTargetDate] = useState(implementation.target_date ?? '');
  const [description, setDescription] = useState(implementation.description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (name.trim().length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const next = await updateImplementation(implementation.id, {
        name: name.trim(),
        customer: customer.trim() || null,
        target_date: targetDate || null,
        description: description.trim() || null,
      });
      onSaved(next);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save the implementation'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className="grid gap-3 rounded-md border p-4 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="grid gap-1.5 sm:col-span-2">
        <Label htmlFor="impl-edit-name">Name</Label>
        <Input id="impl-edit-name" value={name} onChange={(event) => setName(event.target.value)} required />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="impl-edit-customer">Customer</Label>
        <Input id="impl-edit-customer" value={customer} onChange={(event) => setCustomer(event.target.value)} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="impl-edit-target">Target date</Label>
        <Input id="impl-edit-target" type="date" value={targetDate} onChange={(event) => setTargetDate(event.target.value)} />
      </div>
      <div className="grid gap-1.5 sm:col-span-2">
        <Label htmlFor="impl-edit-description">Description</Label>
        <Textarea id="impl-edit-description" rows={3} value={description} onChange={(event) => setDescription(event.target.value)} />
      </div>
      {error ? <p className="text-sm text-destructive sm:col-span-2">{error}</p> : null}
      <div className="flex items-center gap-2 sm:col-span-2">
        <Button type="submit" size="sm" disabled={saving || name.trim().length === 0}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

const DELIVERY_STAGES = new Set(['in_progress', 'ready_for_deployment', 'deployment_approval', 'scheduled', 'deployed', 'validation', 'completed']);
const RAG_TEXT: Record<string, string> = { green: 'On track', at_risk: 'At risk', blocked: 'Blocked' };

/** The one thing a request is waiting on: a hard blocker first, then the first warning. */
function nextStep(row: ImplementationRequest, blockers: ImplementationDetail['blockers']): string | null {
  const own = blockers.filter((blocker) => blocker.work_item_id === row.work_item.id);
  return (own.find((blocker) => blocker.hard) ?? own[0])?.message ?? row.blockers[0] ?? null;
}

function RequestRow({ row, blockers, from }: { row: ImplementationRequest; blockers: ImplementationDetail['blockers']; from: ServiceRequestBackTarget }) {
  const item = row.work_item;
  const next = nextStep(row, blockers);
  const delivering = DELIVERY_STAGES.has(String(item.stage));
  return (
    <li>
      <Link
        to={serviceRequestPath(item)}
        state={{ from }}
        className="grid grid-cols-1 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 transition hover:border-slate-300 hover:shadow-sm hover:no-underline sm:grid-cols-[minmax(0,1fr)_10rem_9rem_7rem] dark:border-slate-800 dark:bg-slate-900"
      >
        <span className="min-w-0">
          <span className="flex min-w-0 items-baseline gap-2">
            <span className="shrink-0 font-mono text-[11px] font-semibold tabular-nums text-primary">{item.key}</span>
            <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100" title={item.title}>{item.title}</span>
          </span>
          <span className="mt-0.5 block truncate text-xs text-slate-500" title={next ?? undefined}>
            {next ? `Next: ${next.replace(/\.$/, '')}` : 'Nothing waiting'}
            {row.target_go_live_date ? ` · Go-live ${formatDateOnly(row.target_go_live_date)}` : ''}
          </span>
        </span>
        <span><StageChip stage={item.stage} name={item.state_name} /></span>
        <span>
          {delivering
            ? <ReadinessBar percent={row.readiness_percent} />
            : <span className="text-xs text-slate-500">Before delivery</span>}
        </span>
        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300">
          {delivering || row.rag === 'blocked' ? (
            <>
              <span className={cn('h-2 w-2 rounded-full', ragDotClass(row.rag))} aria-hidden />
              {RAG_TEXT[row.rag] ?? row.rag}
            </>
          ) : <span className="font-normal text-slate-400">—</span>}
        </span>
      </Link>
    </li>
  );
}

function RequestList({ detail }: { detail: ImplementationDetail }) {
  if (detail.requests.length === 0) {
    return (
      <ListEmpty
        title="No linked requests"
        description="Link a Service Request to this implementation from the request's page."
      />
    );
  }
  const order = (stage: string) => {
    const index = SERVICE_REQUEST_STAGES.indexOf(stage as (typeof SERVICE_REQUEST_STAGES)[number]);
    return index === -1 ? 99 : index;
  };
  const from = { path: implementationPath(detail.implementation.id), label: detail.implementation.name };
  const rows = [...detail.requests].sort((a, b) => order(String(b.work_item.stage)) - order(String(a.work_item.stage)));
  return (
    <ul className="space-y-2" aria-label="Requests in this implementation">
      {rows.map((row) => <RequestRow key={row.work_item.id} row={row} blockers={detail.blockers} from={from} />)}
    </ul>
  );
}

export default function ServiceRequestImplementation() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<ImplementationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const today = startOfDay(new Date());

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setDetail(await fetchImplementation(id));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load the implementation'));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    function onChanged() {
      void load();
    }
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => window.removeEventListener('bb:work-items-changed', onChanged);
  }, [load]);

  const implementation = detail?.implementation ?? null;
  const targetOverdue = implementation ? isOverdue(implementation.target_date, today) : false;

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col gap-4">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 h-7 px-2 text-xs text-muted-foreground">
            <Link to="/service-requests/implementations">
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
              Implementations
            </Link>
          </Button>
        </div>
        <ServiceRequestNav active="implementations" />

        {error ? <ErrorAlert>{error}</ErrorAlert> : null}

        {loading ? (
          <LoadingRows columns={4} />
        ) : !detail || !implementation ? null : (
          <>
            <header className={cn(SR_PANEL, 'flex flex-col gap-4 p-5 lg:flex-row lg:items-start lg:justify-between')}>
              <div className="min-w-0 flex-1">
                {editing ? (
                  <DetailsForm
                    detail={detail}
                    onSaved={(next) => {
                      setDetail(next);
                      setEditing(false);
                    }}
                    onCancel={() => setEditing(false)}
                  />
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <h1 className="truncate text-[22px] font-semibold leading-7 tracking-tight">{implementation.name}</h1>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-muted-foreground"
                        onClick={() => setEditing(true)}
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                        Edit
                      </Button>
                    </div>
                    <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                      <div className="flex items-baseline gap-1.5">
                        <dt className="text-xs text-muted-foreground">Customer</dt>
                        <dd>{implementation.customer || <span className="text-muted-foreground/50">—</span>}</dd>
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <dt className="text-xs text-muted-foreground">Target date</dt>
                        <dd
                          className={cn(
                            'font-mono text-xs tabular-nums',
                            targetOverdue && 'rounded-md bg-red-50 px-1.5 py-0.5 text-red-700 dark:bg-red-950 dark:text-red-300',
                          )}
                        >
                          {implementation.target_date
                            ? formatDateOnly(implementation.target_date) ?? implementation.target_date
                            : '—'}
                        </dd>
                      </div>
                    </dl>
                    {implementation.description ? (
                      <p className="mt-2 max-w-prose whitespace-pre-wrap text-sm text-muted-foreground">{implementation.description}</p>
                    ) : null}
                  </>
                )}
              </div>
              <div className="w-full shrink-0 lg:w-72">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Implementation readiness</p>
                <p className="mt-1 text-4xl font-semibold tabular-nums tracking-tight">{formatPercent(detail.readiness_percent)}</p>
                <ReadinessBar percent={detail.readiness_percent} size="lg" className="mt-2" />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Across {detail.request_count} linked {detail.request_count === 1 ? 'request' : 'requests'}
                </p>
              </div>
            </header>

            <section className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="text-base font-semibold">Requests</h2>
                <span className="text-xs text-slate-500">
                  {detail.request_count} {detail.request_count === 1 ? 'request' : 'requests'}
                  {detail.blocked_count ? ` · ${detail.blocked_count} blocked` : ''}
                </span>
              </div>
              <RequestList detail={detail} />
            </section>
          </>
        )}
      </div>
    </PageFrame>
  );
}
